import ScanProgress from './ScanProgress';

/**
 * Full-screen overlay shown while the live AI tests (visibility / footprint) run.
 * These calls take ~20–40s of real web search, so a tiny inline spinner wasn't
 * enough signal that work is happening (#4) — this dims the page and shows a large
 * animated emblem plus the staged progress so it's obvious the scan is still busy.
 */
export default function LiveTestOverlay({ phase }: { phase: 'visibility' | 'footprint' | 'faq' }): JSX.Element {
  const title = phase === 'visibility' ? 'Testing your visibility' : phase === 'footprint' ? 'Checking your footprint' : 'Drafting your FAQ answers';
  const label = phase === 'visibility' ? 'Asking AI (web search)' : phase === 'footprint' ? 'Checking where AI knows you' : 'Reading your content';
  const stages =
    phase === 'visibility'
      ? [
          'Reading your key pages',
          'Generating the real questions buyers ask',
          'Running live web searches',
          'Detecting mentions vs citations',
          'Mapping competitors',
        ]
      : phase === 'footprint'
      ? [
          'Asking the model what it knows about your brand',
          'Collecting its suggested questions',
          'Verifying them in live web search',
          'Confirming real footholds',
        ]
      : [
          'Reading your key pages',
          'Understanding what you do',
          'Drafting grounded answers',
          'Formatting for FAQPage schema',
        ];

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/75 backdrop-blur-sm px-6">
      <div className="w-full max-w-lg text-center">
        {/* Large animated emblem — concentric spinning ring + pulse */}
        <div className="relative mx-auto mb-6 h-28 w-28" aria-hidden="true">
          <span className="absolute inset-0 rounded-full border-4 border-cyan/20" />
          <span className="absolute inset-0 rounded-full border-4 border-transparent border-t-cyan animate-spin" />
          <span className="absolute inset-3 rounded-full bg-cyan/10 animate-ping" />
          <span className="absolute inset-0 flex items-center justify-center font-mono text-sm text-cyan">AI</span>
        </div>
        <p className="font-serif text-2xl mb-1">{title}…</p>
        <p className="text-sm text-text-secondary mb-6">
          Running live AI tests with web search. This takes ~20–40s. Keep this tab open.
        </p>
        <ScanProgress label={label} stages={stages} />
      </div>
    </div>
  );
}
